import React, { useMemo, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  type Exemplar as ExemplarType,
  EndpointInvestigationException,
} from '@hud/portal-dtos/issues-v2.dtos';
import { formatDateTime, isDefined } from '../utils';
import TableContainer from '../Container/TableContainer';
import { HorizontalContainer } from '../Container/HorizontalContainer';
import { DefaultTable, LinkCell } from '../Table/Table';
import LongContentPopover from '../LongContentPopover/LongContentPopover';
import { Popover } from '../Popover/Popover';
import { Button } from '../Button/Button';
import { HudIcon } from '../Icons/HudIcons';
import {
  CellIcon,
  EntitiesList,
  StyledMoreItemButton,
  StyledMoreItemsContainer,
  TitleContainer,
  JsonContent,
  StyledModal,
  ModalTitle,
} from './Exemplar.styles';

const columnHelper = createColumnHelper<EndpointInvestigationException>();

const Exemplar = ({ exemplar }: { exemplar: ExemplarType }) => {
  const [isForensicsModalOpen, setIsForensicsModalOpen] = useState(false);
  const [buttonText, setButtonText] = useState('Copy to Clipboard');
  const [isCopied, setIsCopied] = useState(false);

  const traceIds = exemplar.data.context.observability_identifiers;

  const handleOpenForensicsModal = () => {
    setIsForensicsModalOpen(true);
  };

  const handleCloseForensicsModal = () => {
    setIsForensicsModalOpen(false);
  };
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(exemplar.data, null, 2));
      setButtonText('JSON Copied!');
      setIsCopied(true);

      // Reset button text after 5 seconds
      setTimeout(() => {
        setButtonText('Copy to Clipboard');
        setIsCopied(false);
      }, 5000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const columns = useMemo(() => {
    const shouldShowMessage = !exemplar.exceptions.every(
      (exception) => !exception.message,
    );

    return [
      columnHelper.accessor('name', {
        header: 'Exception Name',
        minSize: 180,
        maxSize: 400,
      }),
      shouldShowMessage
        ? columnHelper.accessor('message', {
            header: 'Exception Message',
            minSize: 200,
            maxSize: 800,
          })
        : undefined,
      columnHelper.accessor('functions', {
        header: 'Functions',
        minSize: 200,
        maxSize: 800,
        cell: ({ row }) => <ExceptionFunctions functions={row.original.functions} />,
      }),
    ].filter(isDefined);
  }, [exemplar.exceptions]);

  const table = useReactTable({
    data: exemplar.exceptions,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onChange',
    enableSorting: false,
    defaultColumn: {
      minSize: 80,
    },
  });

  return (
    <>
      <TableContainer.Container aria-label="Forensic" role="region">
        <TitleContainer>
          <div className="forensicsTitle">
            <div className="left">
              Forensics
              <span className="timestamp" aria-label="Timestamp">
                {formatDateTime(exemplar.timestamp)}
              </span>
            </div>
            <Button onClick={handleOpenForensicsModal}>View JSON</Button>
          </div>

          {traceIds && (
            <div className="traceIds">
              {Object.entries(traceIds).map(([type, value]) => {
                const label = type
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (char) => char.toUpperCase());

                return (
                  <div key={type}>
                    <span>
                      {label}: {value}
                    </span>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(value);
                      }}
                    >
                      <HudIcon icon="designSystem.copy" size="16px" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </TitleContainer>
        <HorizontalContainer>
          <DefaultTable
            table={table}
            label="Endpoint Investigation Exceptions"
            loading={false}
            loadingRows={5}
          />
        </HorizontalContainer>
        <TableContainer.Footer>&nbsp;</TableContainer.Footer>
      </TableContainer.Container>

      <ForensicsModal opened={isForensicsModalOpen} onClose={handleCloseForensicsModal}>
        <ModalTitle>
          <div className="jsonTitle">
            Forensics
            <span className="timestamp">{formatDateTime(exemplar.timestamp)}</span>
          </div>
          <Button onClick={handleCopyToClipboard}>
            <HudIcon
              icon={isCopied ? 'designSystem.check' : 'designSystem.copy'}
              size="20px"
            />
            {buttonText}
          </Button>
        </ModalTitle>
        <JsonContent>
          <SyntaxHighlighter
            language="json"
            style={tomorrow}
            wrapLines
            wrapLongLines={true}
            showLineNumbers={true}
          >
            {JSON.stringify(exemplar.data, null, 2)}
          </SyntaxHighlighter>
        </JsonContent>
      </ForensicsModal>
    </>
  );
};

export function ExceptionFunctions({
  functions,
}: {
  functions: EndpointInvestigationException['functions'];
}) {
  const firstItem = functions[0];
  const moreItems = functions.slice(1);

  return (
    <>
      <div style={{ paddingRight: '32px' }}>
        <LongContentPopover>
          <CellIcon icon="other.codeBrackets" />
          <LinkCell to={`/explore/functions/${firstItem.id}`}>
            {firstItem.signature}
          </LinkCell>
        </LongContentPopover>
      </div>
      {moreItems && moreItems.length > 0 && (
        <StyledMoreItemsContainer>
          <Popover
            width="300px"
            body={
              <EntitiesList>
                {moreItems.map((entity) => (
                  <LongContentPopover>
                    <CellIcon icon="other.codeBrackets" />
                    <LinkCell to={`/explore/functions/${entity.id}`}>
                      {entity.signature}
                    </LinkCell>
                  </LongContentPopover>
                ))}
              </EntitiesList>
            }
          >
            <StyledMoreItemButton>+{moreItems.length}</StyledMoreItemButton>
          </Popover>
        </StyledMoreItemsContainer>
      )}
    </>
  );
}

function ForensicsModal({
  opened,
  onClose,
  children,
}: {
  opened: boolean;
  onClose: () => void;
} & React.PropsWithChildren) {
  return (
    <StyledModal
      opened={opened}
      withClickOutsideToClose
      onClose={onClose}
      className="forensics-modal"
    >
      <div>{children}</div>
    </StyledModal>
  );
}

export default Exemplar;
